import type {
  GetDuplicateCompaniesRequestType,
  GetDuplicateCompaniesResponseType,
  ExecuteRegistryMergeFromAcquisitionsRequestType,
  ExecuteRegistryMergeFromAcquisitionsResponseType,
  RegistryAcquisitionResponseType,
  RegistryAcquisitionType,
  RegistryAutoRequestType,
  RegistryDeletionRequestType,
  RegistryDeletionResponseType,
  RegistryGetListItemType,
  RegistryGetListPostRequestType,
  RegistryGetListPostResponseType,
  RegistryGetListRequestType,
  RegistryGetListResponseType,
  RegistryReacquisitionRequestType,
  RegistryReacquisitionResponseType,
  RegistrySearchRequestType,
  RegistrySearchResponseType,
} from '@/shared/types/api/registry/api.registries';

import { axiosInstance } from '@/shared/api/axios';

const mapAcquisition = (data: RegistryAcquisitionResponseType): RegistryAcquisitionType => {
  return {
    id: data.id,
    executionHistoryId: data.executionHistoryId,
    extractionHistoryId: data.extractionHistoryId,
    executeType: data.executeType,
    fileId: data.fileId ?? null,
    companyInformationId: data.companyInformationId ?? null,
    acquisitionStatus: data.acquisitionStatus,
    complianceExecutionHistoryId: data.complianceExecutionHistoryId ?? null,
    complianceCustomerId: data.complianceCustomerId ?? null,
    corporateNumber: data.corporateNumber,
    companyName: data.companyName,
    representativeName: data.representativeName,
    address: data.address,
    message: data.message,
    updatedBy: data.updatedBy,
    updatedByUsername: data.updatedByUsername,
    updatedAt: data.updatedAt,
  };
};

type RegistrySearchApiResponse = {
  acquisitions: {
    acquisitions: RegistryAcquisitionResponseType[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
    };
  };
};

export const searchRegistries = async ({
  groupId,
  pageIndex,
  pageSize,
  searchQuery,
  executeType,
  acquisitionStatus,
  operatorId,
  companyInformationId,
  executionHistoryId,
  complianceCustomerId,
  toukiDateFrom,
  toukiDateTo,
  orderBy,
  orderDirection,
}: RegistrySearchRequestType): Promise<RegistrySearchResponseType> => {
  const requestBody: Record<string, unknown> = {
    groupId,
    pageSize,
    pageIndex,
  };

  if (searchQuery) requestBody.searchQuery = searchQuery;
  if (executeType) requestBody.executeType = executeType;
  if (typeof acquisitionStatus === 'number') requestBody.acquisitionStatus = acquisitionStatus;
  if (typeof operatorId === 'number') requestBody.operatorId = operatorId;
  if (typeof companyInformationId === 'number')
    requestBody.companyInformationId = companyInformationId;
  if (typeof executionHistoryId === 'number') requestBody.executionHistoryId = executionHistoryId;
  if (typeof complianceCustomerId === 'number')
    requestBody.complianceCustomerId = complianceCustomerId;
  if (toukiDateFrom) requestBody.toukiDateFrom = toukiDateFrom;
  if (toukiDateTo) requestBody.toukiDateTo = toukiDateTo;
  if (orderBy) requestBody.orderBy = orderBy;
  if (orderDirection) requestBody.orderDirection = orderDirection;

  const { data } = await axiosInstance.post<RegistrySearchApiResponse>(
    `/touki/registries/list/`,
    requestBody,
  );

  return {
    total: data.acquisitions.pagination.total,
    limit: data.acquisitions.pagination.limit,
    offset: data.acquisitions.pagination.offset,
    rows: data.acquisitions.acquisitions.map(mapAcquisition),
  };
};

export const reacquisition = async ({
  groupId,
  acquisitions,
}: RegistryReacquisitionRequestType): Promise<RegistryReacquisitionResponseType> => {
  type RegistryReacquisitionApiResponse = {
    createdAcquisitions: {
      id: number;
      executionHistoryId: number;
      companyName: string;
    }[];
    message: string;
  };

  const { data } = await axiosInstance.post<RegistryReacquisitionApiResponse>(
    `/registries/re-getregistries/`,
    {
      groupId,
      acquisitions: acquisitions.map((acquisition) => ({
        corporateNumber: acquisition.corporateNumber,
        companyName: acquisition.companyName,
        representativeName: acquisition.representativeName,
        address: acquisition.address,
        complianceExecutionHistoryId: acquisition.complianceExecutionHistoryId,
        complianceCustomerId: acquisition.complianceCustomerId,
      })),
    },
  );

  return {
    createdAcquisitions: data.createdAcquisitions.map((item) => ({
      id: item.id,
      executionHistoryId: item.executionHistoryId,
      companyName: item.companyName,
    })),
    message: data.message,
  };
};

export const deleteRegistryGets = async ({
  acquisitionIds,
}: RegistryDeletionRequestType): Promise<RegistryDeletionResponseType> => {
  const { data } = await axiosInstance.delete<RegistryDeletionResponseType>(
    `/touki/registries/acquisition-list`,
    {
      data: {
        acquisitionIds,
      },
    },
  );

  return data;
};

export const getRegistriesAuto = async ({
  groupId,
  acquisitionIds,
}: RegistryAutoRequestType): Promise<void> => {
  await axiosInstance.post(`/registries/getregistries/auto/`, {
    groupId,
    acquisitionIds,
  });
};

export const getRegistryGets = async ({
  groupId,
  executionHistoryId,
  complianceCustomerId,
}: RegistryGetListRequestType): Promise<RegistryGetListResponseType> => {
  type RegistryGetListApiResponse = {
    acquisitions: {
      id: number;
      executionHistoryId?: number;
      corporateNumber: string;
      companyName: string;
      representativeName: string;
      address: string;
      acquisitionStatus?: number;
    }[];
  };

  const { data } = await axiosInstance.get<RegistryGetListApiResponse>(
    `/touki/registries/acquisition-list`,
    {
      params: {
        groupId,
        executionHistoryId,
        complianceCustomerId,
      },
    },
  );

  const acquisitions: RegistryGetListItemType[] = data.acquisitions.map((item) => ({
    id: item.id,
    executionHistoryId: item.executionHistoryId || 0,
    corporateNumber: item.corporateNumber,
    companyName: item.companyName,
    representativeName: item.representativeName,
    address: item.address,
  }));

  return { acquisitions };
};

export const postRegistryGets = async ({
  groupId,
  acquisitions,
}: RegistryGetListPostRequestType): Promise<RegistryGetListPostResponseType> => {
  type RegistryGetListPostApiResponse = {
    createdAcquisitions: {
      id: number;
    }[];
    message: string;
  };

  const { data } = await axiosInstance.post<RegistryGetListPostApiResponse>(
    `/touki/registries/acquisition-list`,
    {
      groupId,
      acquisitions: acquisitions.map((acquisition) => ({
        corporateNumber: acquisition.corporateNumber,
        companyName: acquisition.companyName,
        representativeName: acquisition.representativeName,
        address: acquisition.address,
        complianceExecutionHistoryId: acquisition.complianceExecutionHistoryId,
        complianceCustomerId: acquisition.complianceCustomerId,
      })),
    },
  );

  return {
    createdAcquisitions: data.createdAcquisitions.map((acquisition, index) => ({
      id: acquisition.id,
      executionHistoryId: 0,
      companyName: acquisitions[index]?.companyName || '',
    })),
  };
};

export const getDuplicateCompanies = async ({
  complianceExecutionHistoryId,
}: GetDuplicateCompaniesRequestType): Promise<GetDuplicateCompaniesResponseType> => {
  const { data } = await axiosInstance.get<GetDuplicateCompaniesResponseType>(
    `/touki/duplicate-companies/`,
    {
      params: {
        complianceExecutionHistoryId,
      },
    },
  );

  return data;
};

export const executeRegistryMergeFromAcquisitions = async ({
  acquisitionIds,
}: ExecuteRegistryMergeFromAcquisitionsRequestType): Promise<ExecuteRegistryMergeFromAcquisitionsResponseType> => {
  const { data } = await axiosInstance.post<ExecuteRegistryMergeFromAcquisitionsResponseType>(
    `/touki/compliance/merge/execute/single`,
    {
      acquisitionIds,
    },
  );

  return data;
};
